import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Copy, Crown, Users, Play, LogOut, Share2, Link as LinkIcon } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useGameStore } from '@/lib/gameStore';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { mapDatabaseError } from '@/lib/errorUtils';
import PlayerAvatar from './PlayerAvatar';

type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';

interface LobbyProps {
  onStartGame: () => void;
  onLeave: () => void;
  setMusicIntensity: (intensity: MusicIntensity) => void;
}

export default function Lobby({ onStartGame, onLeave, setMusicIntensity }: LobbyProps) {
  const { currentRoom, playerId, updateRoom } = useGameStore();
  const [showQR, setShowQR] = useState(false);

  const gameLink = `https://sixsevengame.ai/join/${currentRoom?.id || ''}`;
  const { toast } = useToast();
  const [copying, setCopying] = useState(false);

  const isHost = currentRoom?.hostId === playerId;
  const canStart = (currentRoom?.players.length || 0) >= 2;

  // Set waiting music intensity when in lobby
  useEffect(() => {
    setMusicIntensity('waiting');
  }, [setMusicIntensity]);

  useEffect(() => {
    if (!currentRoom) return;

    // Subscribe to player changes
    const playerChannel = supabase
      .channel(`lobby-players:${currentRoom.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'players',
          filter: `room_id=eq.${currentRoom.id}`
        },
        async () => {
          // Fetch updated players
          const { data: players } = await supabase
            .from('players')
            .select('*')
            .eq('room_id', currentRoom.id);

          if (players) {
            updateRoom({
              players: players.map(p => ({
                id: p.id,
                name: p.name,
                isHost: p.is_host,
                stressLevel: p.stress_level as any,
                isActive: p.is_active,
              }))
            });
          }
        }
      )
      .subscribe();

    // Subscribe to room status changes for guests to auto-navigate
    const roomChannel = supabase
      .channel(`lobby-room:${currentRoom.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'rooms',
          filter: `id=eq.${currentRoom.id}`
        },
        (payload) => {
          const newRoom = payload.new as any;
          if (newRoom.status === 'playing') {
            updateRoom({ status: 'playing' });
            onStartGame();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(playerChannel);
      supabase.removeChannel(roomChannel);
    };
  }, [currentRoom?.id, onStartGame]);

  const copyRoomCode = async () => {
    if (!currentRoom) return;
    setCopying(true);
    await navigator.clipboard.writeText(currentRoom.id);
    toast({ title: "Room code copied!", description: "Share with your friends" });
    setTimeout(() => setCopying(false), 2000);
  };

  const copyGameLink = async () => {
    await navigator.clipboard.writeText(gameLink);
    toast({ title: "Link copied! 🔗", description: "Share the link with your friends!" });
  };

  const shareViaWhatsApp = () => {
    const message = `Join me for 67 CHAOS! 🎮🔥\nClick here: ${gameLink}\nOr enter code: ${currentRoom?.id}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleStartGame = async () => {
    if (!currentRoom || !canStart) return;

    try {
      // Verify auth session is still valid
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ 
          title: "Session expired", 
          variant: "destructive", 
          description: "Please rejoin the room" 
        });
        onLeave();
        return;
      }

      const { error } = await supabase
        .from('rooms')
        .update({ status: 'playing', game_start_time: new Date().toISOString() })
        .eq('id', currentRoom.id);

      if (error) {
        console.error('Error starting game:', error);
        toast({ title: "Failed to start game", variant: "destructive", description: mapDatabaseError(error) });
        return;
      }

      // Re-fetch room to verify status changed
      const { data: updatedRoom, error: fetchError } = await supabase
        .from('rooms')
        .select('status')
        .eq('id', currentRoom.id)
        .single();

      if (fetchError || !updatedRoom) {
        toast({ 
          title: "Failed to start game", 
          variant: "destructive", 
          description: "Room verification failed" 
        });
        return;
      }

      if (updatedRoom.status !== 'playing') {
        toast({ 
          title: "Failed to start game", 
          variant: "destructive", 
          description: "Status update verification failed" 
        });
        return;
      }

      // Only proceed if database update succeeded and was verified
      updateRoom({ status: 'playing' });
      onStartGame();
    } catch (error) {
      console.error('Error starting game:', error);
      toast({ title: "Failed to start game", variant: "destructive" });
    }
  };

  const handleLeave = async () => {
    if (!currentRoom || !playerId) return;

    try {
      // Delete player record (RLS policy ensures only owner can delete their own record)
      await supabase
        .from('players')
        .delete()
        .eq('id', playerId);

      // Sign out the anonymous user
      await supabase.auth.signOut();

      onLeave();
    } catch (error) {
      console.error('Error leaving room:', error);
    }
  };

  if (!currentRoom) return null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background via-card to-background" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 w-full max-w-2xl space-y-6"
      >
        {/* Room Code Display */}
        <Card className="glass-card p-8 text-center space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
              Room Code
            </p>
            <h1 className="text-6xl font-black tracking-widest text-primary text-shadow">
              {currentRoom.id}
            </h1>
          </div>

          {/* Sharing Options */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={copyRoomCode}
              variant="outline"
              className="font-bold"
              size="lg"
            >
              <Copy className="mr-2 h-4 w-4" />
              CODE
            </Button>
            
            <Button
              onClick={copyGameLink}
              variant="outline"
              className="font-bold"
              size="lg"
            >
              <LinkIcon className="mr-2 h-4 w-4" />
              LINK
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={shareViaWhatsApp}
              className="font-bold bg-[#25D366] hover:bg-[#1DA851]"
              size="lg"
            >
              <Share2 className="mr-2 h-4 w-4" />
              WHATSAPP
            </Button>

            <Dialog open={showQR} onOpenChange={setShowQR}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="font-bold"
                  size="lg"
                >
                  📱 QR
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-black text-center">SCAN TO JOIN</DialogTitle>
                </DialogHeader>
                <div className="flex justify-center p-6 bg-white rounded-2xl">
                  <QRCodeSVG 
                    value={gameLink} 
                    size={256}
                    level="H"
                    includeMargin
                  />
                </div>
                <p className="text-center text-sm text-muted-foreground font-bold">
                  Scan with your phone to join!
                </p>
              </DialogContent>
            </Dialog>
          </div>
        </Card>

        {/* Players List */}
        <Card className="glass-card p-6 space-y-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-5 w-5" />
            <span className="font-bold uppercase tracking-wide">
              Players ({currentRoom.players.length}/8)
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {currentRoom.players.map((player, index) => (
              <motion.div
                key={player.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col items-center gap-2"
              >
                <PlayerAvatar player={player} />
                <div className="text-center">
                  <p className="font-semibold text-sm truncate max-w-[100px]">
                    {player.name}
                  </p>
                  {player.isHost && (
                    <div className="flex items-center justify-center gap-1 text-xs text-primary">
                      <Crown className="h-3 w-3" />
                      <span>Host</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </Card>

        {/* Actions */}
        <div className="grid gap-3">
          {isHost && (
            <Button
              onClick={handleStartGame}
              disabled={!canStart}
              className="h-16 text-lg font-bold uppercase tracking-wide glow-green hover:scale-105 transition-transform"
            >
              <Play className="mr-2 h-6 w-6" />
              Start Game {!canStart && "(Need 2+ players)"}
            </Button>
          )}

          <Button
            onClick={handleLeave}
            variant="secondary"
            className="h-12 font-bold"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Leave Room
          </Button>
        </div>

        {/* Waiting message for non-hosts */}
        {!isHost && (
          <motion.p
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-center text-muted-foreground font-semibold"
          >
            Waiting for host to start the game...
          </motion.p>
        )}
      </motion.div>
    </div>
  );
}
